/**
 * Client-side S2S JWT signing helper (drift `81f3f606`, wave item #21).
 *
 * Produces an S2S JWT for OUTBOUND gRPC calls and the canonical
 * `authorization: Bearer <jwt>` metadata pair. The full flow:
 *
 *   claims → KMS Sign (RSASSA_PKCS1_V1_5_SHA_256) → RS256 JWT
 *          → grpc-metadata `authorization: Bearer <jwt>`
 *
 * The produced JWT verifies against this package's `verifyS2SToken`
 * (Part A) — sign with the helper, verify with the verifier, claims match.
 *
 * ## ONE surface over LocalStack KMS (test) + prod KMS (prod)
 *
 * The signer wraps a KMS asymmetric SIGN_VERIFY key behind a single
 * surface. It deliberately MIRRORS `@nodii/pii`'s AWS-KMS construction
 * conventions (`AwsKmsClient` / `buildDefaultAwsClients` in
 * `ts/pii/src/aws.ts`): same `region` + optional `endpoint` override +
 * optional explicit `credentials`, with the SAME `KMS_ENDPOINT` env-var
 * fallback and the SAME inert-credentials-when-pointing-at-localstack
 * behaviour. Production omits `endpoint` → real AWS KMS; tests set
 * `KMS_ENDPOINT=http://localhost:4566` (or pass `endpoint`) → LocalStack
 * KMS. No new credential/endpoint convention is invented.
 *
 * Why a DISTINCT KMS client and not literal reuse of `pii.AwsKmsClient`:
 * pii's `KmsClient` interface is envelope-encryption only
 * (`generateDataKey` / `decrypt`) — it has no `Sign` / `GetPublicKey`
 * operation, so it physically cannot mint a JWT signature. This helper
 * adds the `kms.Sign` / `kms.GetPublicKey` surface using the IDENTICAL
 * construction pattern, rather than forking pii's envelope code.
 *
 * Key MANAGEMENT (CreateKey, alias, rotation, IAM) stays caller-owned, the
 * same way `@nodii/pii` keeps tenant-CMK provisioning out of the lib — the
 * signer only Signs + reads the public key.
 */
import {
  GetPublicKeyCommand,
  KMSClient,
  SignCommand,
} from "@aws-sdk/client-kms";
import type { JWK } from "jose";
import { exportJWK, importSPKI } from "jose";

/**
 * AWS construction options — mirrors `@nodii/pii`'s `AwsClientOptions`
 * (region + optional endpoint + optional credentials) verbatim so the
 * localstack-vs-prod selection behaves identically across both libs.
 */
export interface KmsSignerAwsOptions {
  region: string;
  /**
   * Endpoint override for LocalStack / non-prod AWS-compatible targets
   * (e.g. `http://localhost:4566`). Omit in prod → real AWS KMS. Falls
   * back to `process.env.KMS_ENDPOINT` when omitted (parity with
   * `@nodii/pii`'s `buildDefaultAwsClients`).
   */
  endpoint?: string;
  /**
   * Explicit credentials. When omitted AND an endpoint is in play
   * (localstack), inert `test`/`test` creds are forced — same guard as
   * `@nodii/pii`, so the default credential chain never proxies real AWS
   * keys to localstack. In prod (no endpoint) the SDK's default chain is
   * used.
   */
  credentials?: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  };
}

/** Options for {@link S2SJwtSigner}. */
export interface S2SJwtSignerOptions {
  /**
   * AWS construction options OR a pre-built `@aws-sdk/client-kms`
   * `KMSClient`. Prefer the options form; pass a client only when you
   * already manage one (parity with `@nodii/pii` accepting an explicit
   * `kmsClient`).
   */
  aws?: KmsSignerAwsOptions;
  /** Pre-built KMS client (overrides `aws`). */
  kmsClient?: KMSClient;
  /**
   * KMS key id / ARN / alias of the asymmetric RSA SIGN_VERIFY key used
   * to sign. (Create it with `KeySpec=RSA_2048, KeyUsage=SIGN_VERIFY`.)
   */
  keyId: string;
  /**
   * JWT `kid` header written into every minted token. Drives JWKS key
   * selection on the verifier (D203 — `kid` is mandatory). Defaults to
   * `opts.keyId` when omitted.
   */
  kid?: string;
  /** `iss` claim — the S2S issuer URL/id (ENFORCED on verify per D203). */
  issuer: string;
  /**
   * `svc` claim — the source service's CANONICAL service id (D188:
   * matches the hub `services` registry id verbatim; e.g.
   * `nodii-tenant-service`). This is what the verifier surfaces as
   * `serviceName`.
   */
  serviceName: string;
  /**
   * Optional `sid` claim (service instance / id). When omitted the
   * verifier falls back to `sub` for `serviceId`.
   */
  serviceId?: string;
  /** Default token lifetime in seconds. Default 300 (5 min). */
  defaultTtlSeconds?: number;
  /**
   * Clock injection (epoch ms). Defaults to `Date.now`. Tests advance
   * this to mint deliberately-expired tokens for the sad path.
   */
  nowFn?: () => number;
}

/** Per-call claims for {@link S2SJwtSigner.sign}. */
export interface SignS2SParams {
  /** `aud` claim (ENFORCED on verify per D203). */
  audience: string | string[];
  /** `sub` claim. Defaults to `serviceId ?? serviceName`. */
  subject?: string;
  /** Scopes → space-delimited `scope` claim (the verifier parses both shapes). */
  scopes?: string[];
  /** Optional `instance_id` claim. */
  instanceId?: string;
  /**
   * Optional `jti` claim. Generated via `crypto.randomUUID()` when
   * omitted — REQUIRED whenever the verifier runs with a replay store, so
   * the helper always mints one by default.
   */
  jti?: string;
  /** Override the signer's default TTL for this token (seconds). */
  ttlSeconds?: number;
  /** Extra custom claims merged into the payload (won't override reserved claims). */
  extraClaims?: Record<string, unknown>;
}

const RS256_KMS_ALG = "RSASSA_PKCS1_V1_5_SHA_256" as const;

function base64url(input: Uint8Array | string): string {
  const buf =
    typeof input === "string" ? Buffer.from(input, "utf8") : Buffer.from(input);
  return buf.toString("base64url");
}

/**
 * Resolve a KMS client from options, mirroring `@nodii/pii`'s
 * localstack-vs-prod selection (KMS_ENDPOINT fallback + inert creds when
 * pointing at a non-AWS endpoint).
 */
function buildKmsClient(opts: S2SJwtSignerOptions): KMSClient {
  if (opts.kmsClient) return opts.kmsClient;
  const aws = opts.aws;
  if (!aws) {
    throw new Error(
      "S2SJwtSigner: either `aws` (region + optional endpoint) or `kmsClient` is required",
    );
  }
  const endpoint = aws.endpoint ?? process.env.KMS_ENDPOINT ?? undefined;
  const usingNonAwsEndpoint = Boolean(endpoint);
  const credentials =
    aws.credentials ??
    (usingNonAwsEndpoint
      ? { accessKeyId: "test", secretAccessKey: "test" }
      : undefined);
  return new KMSClient({
    region: aws.region,
    ...(endpoint ? { endpoint } : {}),
    ...(credentials ? { credentials } : {}),
  });
}

/**
 * Mints RS256 S2S JWTs by signing the JWT signing-input with KMS. One
 * surface over LocalStack KMS (test) + prod KMS (prod); see module doc.
 */
export class S2SJwtSigner {
  private readonly kms: KMSClient;
  private readonly keyId: string;
  private readonly kid: string;
  private readonly issuer: string;
  private readonly serviceName: string;
  private readonly serviceId?: string;
  private readonly defaultTtlSeconds: number;
  private readonly nowFn: () => number;

  constructor(opts: S2SJwtSignerOptions) {
    if (!opts.keyId) throw new Error("S2SJwtSigner: keyId is required");
    if (!opts.issuer) throw new Error("S2SJwtSigner: issuer is required");
    if (!opts.serviceName)
      throw new Error("S2SJwtSigner: serviceName is required");
    this.kms = buildKmsClient(opts);
    this.keyId = opts.keyId;
    this.kid = opts.kid ?? opts.keyId;
    this.issuer = opts.issuer;
    this.serviceName = opts.serviceName;
    this.serviceId = opts.serviceId;
    this.defaultTtlSeconds = opts.defaultTtlSeconds ?? 300;
    this.nowFn = opts.nowFn ?? Date.now;
  }

  /**
   * Mint a signed RS256 S2S JWT. Builds the locked claim shape
   * (`iss`/`aud`/`sub`/`iat`/`exp`/`jti`/`svc` + optional `sid`/`scope`/
   * `instance_id`), KMS-signs the `header.payload` signing-input with
   * `RSASSA_PKCS1_V1_5_SHA_256`, and returns the compact JWT string.
   */
  async sign(params: SignS2SParams): Promise<string> {
    const nowSeconds = Math.floor(this.nowFn() / 1000);
    const ttl = params.ttlSeconds ?? this.defaultTtlSeconds;
    const sub = params.subject ?? this.serviceId ?? this.serviceName;

    const header = { alg: "RS256", kid: this.kid, typ: "JWT" } as const;

    const payload: Record<string, unknown> = {
      ...(params.extraClaims ?? {}),
      iss: this.issuer,
      sub,
      aud: params.audience,
      svc: this.serviceName,
      iat: nowSeconds,
      exp: nowSeconds + ttl,
      jti: params.jti ?? crypto.randomUUID(),
    };
    if (this.serviceId) payload.sid = this.serviceId;
    if (params.scopes && params.scopes.length > 0) {
      payload.scope = params.scopes.join(" ");
    }
    if (params.instanceId) payload.instance_id = params.instanceId;

    const signingInput = `${base64url(JSON.stringify(header))}.${base64url(
      JSON.stringify(payload),
    )}`;

    const res = await this.kms.send(
      new SignCommand({
        KeyId: this.keyId,
        Message: Buffer.from(signingInput, "utf8"),
        MessageType: "RAW",
        SigningAlgorithm: RS256_KMS_ALG,
      }),
    );
    if (!res.Signature) {
      throw new Error("S2SJwtSigner.sign: KMS returned no Signature");
    }
    // KMS returns the RSASSA-PKCS1-v1_5 signature as raw DER-free bytes
    // (the bare RSA signature) — which is exactly what JWS RS256 expects,
    // base64url-encoded.
    const signature = base64url(res.Signature);
    return `${signingInput}.${signature}`;
  }

  /**
   * Mint a JWT and return the canonical gRPC metadata pair
   * `{ authorization: "Bearer <jwt>" }`, ready to splat onto outbound
   * `grpc.Metadata` / a call's `metadata` object.
   */
  async signAuthorizationMetadata(
    params: SignS2SParams,
  ): Promise<{ authorization: string }> {
    const token = await this.sign(params);
    return { authorization: `Bearer ${token}` };
  }

  /**
   * Fetch the signing key's PUBLIC half from KMS as a JWK (with `kid` /
   * `alg: RS256` / `use: sig` set per D203), suitable for assembling a
   * static JWKS that the verifier (`createJwksProvider({ staticJwks })`)
   * resolves against. Lets a test round-trip sign → verify with NO
   * `/.well-known/jwks.json` endpoint.
   */
  async exportPublicJwk(): Promise<JWK> {
    const res = await this.kms.send(
      new GetPublicKeyCommand({ KeyId: this.keyId }),
    );
    if (!res.PublicKey) {
      throw new Error(
        "S2SJwtSigner.exportPublicJwk: KMS returned no PublicKey",
      );
    }
    // KMS returns DER (SubjectPublicKeyInfo). Wrap as PEM, import, export JWK.
    const der = Buffer.from(res.PublicKey).toString("base64");
    const pem = `-----BEGIN PUBLIC KEY-----\n${der
      .replace(/(.{64})/g, "$1\n")
      .replace(/\n$/, "")}\n-----END PUBLIC KEY-----`;
    const key = await importSPKI(pem, "RS256", { extractable: true });
    const jwk = await exportJWK(key);
    return { ...jwk, kid: this.kid, alg: "RS256", use: "sig" };
  }

  /** Free the underlying KMS client's sockets. */
  destroy(): void {
    this.kms.destroy();
  }
}

/**
 * Convenience constructor — `new S2SJwtSigner(opts)`. Mirrors the
 * factory ergonomics of `createSigV4S3JWKSet` / `createJwksProvider`.
 */
export function createS2SJwtSigner(opts: S2SJwtSignerOptions): S2SJwtSigner {
  return new S2SJwtSigner(opts);
}
